import { useState, useEffect } from "react";
import MainContainer from "../MainContainer";
import type { User } from "../../types";
import { Bar, Doughnut } from "react-chartjs-2";
import { BarChart, People } from "@mui/icons-material";
import {
  Chart,
  Title,
  Legend,
  Tooltip,
  ArcElement,
  BarElement,
  LinearScale,
  CategoryScale,
  type ChartData,
  type ChartOptions,
} from "chart.js";
import { Box, Card, Grid, Avatar, Divider, Typography, CardHeader, CardContent } from "@mui/material";

Chart.register(CategoryScale, LinearScale, ArcElement, BarElement, Tooltip, Title, Legend);

export default function _TotalsTable(props: { users: User[]; tableTitle: string }) {
  const { users, tableTitle } = props;

  const [state, setState] = useState({
    averageMatch: 0,
    nationalIds: 0,
    passports: 0,
    driversLicenses: 0,
    noDocument: 0,
  });

  const [data, setData] = useState<ChartData<"bar">>({
    datasets: [],
    labels: [],
  });

  const [donutData, setDonutData] = useState<ChartData<"doughnut">>({
    datasets: [],
    labels: ["NATIONAL IDS", "PASSPORTS", "DRIVER'S LICENSES", "UNDEFINED"],
  });

  const donutOptions: ChartOptions<"doughnut"> = {
    animation: false,
    cutout: 80,
    layout: { padding: 0 },
    maintainAspectRatio: false,
    responsive: true,
    plugins: {
      tooltip: {
        borderWidth: 1,
        enabled: true,
      },
    },
  };

  const options: ChartOptions<"bar"> = {
    animation: false,
    layout: { padding: 0 },
    maintainAspectRatio: false,
    responsive: true,
    plugins: {
      tooltip: {
        enabled: true,
      },
    },
  };

  useEffect(() => {
    if (!users || !users.length) {
      return;
    }

    let averageMatch = 0;
    let skippedMatch = 0;
    let nationalIds = 0;
    let passports = 0;
    let noDocument = 0;
    let driversLicenses = 0;
    let dataSets: any = {};
    let passedUsers: any = {};

    users?.forEach((user: any) => {
      user.match ? (averageMatch += user.match) : skippedMatch++;

      const claimType = user.claim_type.toUpperCase();

      if (!dataSets[claimType])
        dataSets[claimType] = {
          approved: 0,
          A2_review: 0,
          pending: 0,
          authenticate: 0,
        };

      if (!passedUsers[user.registrationNumber]) {
        switch (user.currentStatus) {
          case "approved": {
            dataSets[claimType].approved++;
            break;
          }
          case "A2 review": {
            dataSets[claimType].A2_review++;
            break;
          }
          case "pending": {
            dataSets[claimType].pending++;
            break;
          }
          case "authenticate": {
            dataSets[claimType].authenticate++;
            break;
          }
        }

        passedUsers[user.registrationNumber] = true;
      }

      if (!user.documentType || user.documentType === "UNDEFINED") {
        noDocument++;
        return;
      }

      user.documentType === "NATIONAL_ID_CARD"
        ? nationalIds++
        : user.documentType === "PASSPORT"
        ? passports++
        : driversLicenses++;
    });

    const labels: string[] = [];
    const approved: number[] = [];
    const A2_review: number[] = [];
    const pending: number[] = [];
    const authenticate: number[] = [];

    for (const dataSet in dataSets) {
      labels.push(dataSet);
      approved.push(dataSets[dataSet].approved);
      A2_review.push(dataSets[dataSet].A2_review);
      pending.push(dataSets[dataSet].pending);
      authenticate.push(dataSets[dataSet].authenticate);
    }

    setState({
      averageMatch: averageMatch / (users.length - skippedMatch) || 0,
      nationalIds,
      passports,
      driversLicenses,
      noDocument,
    });

    const columnStyle = {
      barPercentage: 1,
      barThickness: 50,
      borderRadius: 4,
      categoryPercentage: 1,
      maxBarThickness: 50,
      minBarLength: 10,
    };

    setData({
      labels,
      datasets: [
        {
          ...columnStyle,
          data: approved,
          label: "APPROVED",
          backgroundColor: "green",
        },
        {
          ...columnStyle,
          data: A2_review,
          label: "A2 REVIEW",
          backgroundColor: "red",
        },
        {
          ...columnStyle,
          data: pending,
          label: "PENDING",
          backgroundColor: "gray",
        },
      ],
    });

    setDonutData({
      ...donutData,
      datasets: [
        {
          data: [nationalIds, passports, driversLicenses, noDocument],
          backgroundColor: ["orange", "blue", "purple", "grey"],
          borderWidth: 8,
          borderColor: "white",
          hoverBorderColor: "white",
          label: "  Documents",
        },
      ],
    });
  }, [users]);

  return (
    <MainContainer title={tableTitle} titleVariant="h4">
      <Grid container spacing={3} justifyContent="center" sx={{ mt: 3 }}>
        <Grid item lg={6} sm={6} xl={6} xs={6}>
          <Card>
            <CardContent>
              <Grid container spacing={3} justifyContent="space-between">
                <Grid item>
                  <Typography color="textSecondary" gutterBottom variant="h6">
                    TOTAL ENTRIES
                  </Typography>

                  <Typography color="textPrimary" variant="h3">
                    {users ? users.length : 0}
                  </Typography>
                </Grid>

                <Grid item>
                  <Avatar>
                    <People />
                  </Avatar>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        <Grid item lg={6} sm={6} xl={6} xs={6}>
          <Card>
            <CardContent>
              <Grid container spacing={3} justifyContent="space-between">
                <Grid item>
                  <Typography color="textSecondary" gutterBottom variant="h6">
                    AVERAGE MATCH SCORE
                  </Typography>

                  <Typography color="textPrimary" variant="h3">
                    {state.averageMatch.toFixed(2)}%
                  </Typography>
                </Grid>

                <Grid item>
                  <Avatar>
                    <BarChart />
                  </Avatar>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        <Grid item lg={12} md={12} xl={12} xs={12}>
          <Card>
            <CardHeader title="STATUSES" />

            <Divider />

            <CardContent>
              <Box
                sx={{
                  height: 600,
                  position: "relative",
                }}
              >
                <Bar data={data} options={options} />
              </Box>
            </CardContent>
            <Divider />
          </Card>
        </Grid>

        <Grid item lg={12} md={12} xl={12} xs={12}>
          <Card>
            <CardHeader title="DOCUMENTS" />

            <Divider />

            <CardContent>
              <Box
                sx={{
                  height: 435,
                  position: "relative",
                }}
              >
                <Doughnut data={donutData} options={donutOptions} />
              </Box>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  pt: 2,
                }}
              >
                <Box
                  sx={{
                    p: 1,
                    textAlign: "center",
                  }}
                >
                  <Typography color="textPrimary" variant="body2">
                    NATIONAL IDS
                  </Typography>

                  <Typography style={{ color: "orange" }} variant="h4">
                    {users ? ((state.nationalIds / users?.length) * 100).toFixed(2) : 0}%
                  </Typography>
                </Box>

                <Box
                  sx={{
                    p: 1,
                    textAlign: "center",
                  }}
                >
                  <Typography color="textPrimary" variant="body2">
                    PASSPORTS
                  </Typography>

                  <Typography style={{ color: "blue" }} variant="h4">
                    {users ? ((state.passports / users?.length) * 100).toFixed(2) : 0}%
                  </Typography>
                </Box>

                <Box
                  sx={{
                    p: 1,
                    textAlign: "center",
                  }}
                >
                  <Typography color="textPrimary" variant="body2">
                    DRIVER'S LICENCES
                  </Typography>

                  <Typography style={{ color: "purple" }} variant="h4">
                    {users ? ((state.driversLicenses / users?.length) * 100).toFixed(2) : 0}%
                  </Typography>
                </Box>

                <Box
                  sx={{
                    p: 1,
                    textAlign: "center",
                  }}
                >
                  <Typography color="textPrimary" variant="body2">
                    UNDEFINED
                  </Typography>

                  <Typography style={{ color: "grey" }} variant="h4">
                    {users ? ((state.noDocument / users?.length) * 100).toFixed(2) : 0}%
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </MainContainer>
  );
}
